import React, { useState } from 'react';
import { Button } from './ui/button';
import { Send, X, Mail, Loader2, Copy, Check } from 'lucide-react';

/**
 * RequestDocsButton — opens a modal that lets an agent send a tokenised secure
 * upload link to a client. Clients then upload to /client-upload/:token where
 * each file is auto-categorised + filename-parsed for owner/company.
 */
export default function RequestDocsButton({ folder, apiUrl }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(folder.client_email || '');
  const [name, setName] = useState(folder.client_name || '');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');

  const reset = () => {
    setOpen(false);
    setResult(null);
    setErr('');
    setMessage('');
    setCopied(false);
  };

  const send = async (e) => {
    e.preventDefault();
    setErr('');
    if (!email || !email.includes('@')) { setErr('Please enter a valid client email.'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${apiUrl}/api/client-files/${folder.id}/doc-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_email: email.trim(), client_name: name.trim() || null, custom_message: message.trim() || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed to create request');
      setResult(d);
    } catch (ex) {
      setErr(ex.message || 'Failed to create request');
    }
    setSubmitting(false);
  };

  const copyLink = async () => {
    if (!result?.link) return;
    await navigator.clipboard.writeText(result.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        className="border-[#D4AF37] text-[#003D5C] hover:bg-[#FFF8E1]"
        data-testid="request-docs-btn"
      >
        <Send className="h-4 w-4 mr-2" />
        Request Documents
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="request-docs-modal">
            <div className="p-5 border-b flex items-center justify-between bg-gradient-to-r from-[#003D5C] to-[#0077B6] text-white rounded-t-xl">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Send className="h-5 w-5" /> Request Documents
              </h2>
              <button onClick={reset} className="text-white/80 hover:text-white text-xl leading-none">×</button>
            </div>

            {!result ? (
              <form onSubmit={send} className="p-5 space-y-4">
                <p className="text-sm text-gray-600">
                  We'll email <b>{folder.client_name || 'your client'}</b> a secure link. Their uploads will land in
                  this folder, auto-categorised by Income / Down Payment / Consent / ID / Other.
                </p>

                {err && <div className="bg-red-50 border-l-4 border-red-500 text-red-800 px-3 py-2 rounded text-sm">{err}</div>}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client email *</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="client@example.com"
                    className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6]"
                    required
                    data-testid="request-docs-email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Optional — defaults to folder client name"
                    className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom message (optional)</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="e.g. Please send your last 2 paystubs and most recent NOA…"
                    rows={3}
                    className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6]"
                    data-testid="request-docs-message"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button type="button" variant="outline" onClick={reset}>Cancel</Button>
                  <Button type="submit" disabled={submitting} className="bg-[#0077B6]" data-testid="send-request-btn">
                    {submitting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Sending…</> : <><Mail className="h-4 w-4 mr-1" />Send Request</>}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="p-5 space-y-4" data-testid="request-docs-result">
                <div className="bg-green-50 border-l-4 border-green-500 text-green-900 p-3 rounded text-sm">
                  {result.email_sent ? (
                    <>Request sent! An email is on its way to <b>{email}</b>.</>
                  ) : (
                    <>Request created, but the email failed. Copy the link below and send it via WhatsApp / SMS.</>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Secure upload link</p>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={result.link}
                      className="flex-1 border rounded px-3 py-2 text-xs font-mono bg-gray-50"
                      onClick={(e) => e.target.select()}
                    />
                    <Button onClick={copyLink} variant="outline" data-testid="copy-link-btn">
                      {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Expires in 14 days. You can cancel anytime.</p>
                </div>
                <div className="flex justify-end pt-2 border-t">
                  <Button onClick={reset} className="bg-[#0077B6]">Done</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ---------- Category badge ----------
const CATEGORY_STYLES = {
  income:               { label: 'Income',           bg: 'bg-emerald-100', text: 'text-emerald-800' },
  down_payment_closing: { label: 'Down Payment',     bg: 'bg-amber-100',   text: 'text-amber-800' },
  consent_disclosure:   { label: 'Consent',          bg: 'bg-purple-100',  text: 'text-purple-800' },
  id:                   { label: 'ID',               bg: 'bg-rose-100',    text: 'text-rose-800' },
  other:                { label: 'Other',            bg: 'bg-gray-100',    text: 'text-gray-700' },
};

export function CategoryBadge({ category, source }) {
  if (!category) return null;
  const s = CATEGORY_STYLES[category] || CATEGORY_STYLES.other;
  return (
    <span
      className={`text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}
      title={source === 'ai' ? 'Auto-categorised by AI' : source === 'filename' ? 'Detected from filename' : 'Categorised'}
      data-testid={`category-badge-${category}`}
    >
      {s.label}
    </span>
  );
}
